import type { BranchFilterCondition, BranchFilterGroup } from '@/types/filters';
import type { PlayFieldDescriptor } from '@/types/custom-playbooks';
import { operatorLabel } from '@/utils/playbook-trigger.utils';

const DAY_OPERATORS = new Set([
  'within_last_days',
  'more_than_days_ago',
  'within_next_days',
]);

/** Company Domain against a saved Cleanlist list; the value is the list id. */
export const LIST_OPERATORS: readonly string[] = ['in_list', 'not_in_list'];

export function isBranchDate(value: unknown): boolean {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    Number(value.slice(0, 4)) >= 1 &&
    Number.isFinite(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
}

export function branchConditionComplete(leaf: BranchFilterCondition): boolean {
  if (
    !leaf ||
    typeof leaf.field !== 'string' ||
    !leaf.field.trim() ||
    typeof leaf.operator !== 'string' ||
    !leaf.operator
  )
    return false;
  if (leaf.operator === 'exists' || leaf.operator === 'not_exists') return true;
  if (LIST_OPERATORS.includes(leaf.operator))
    return typeof leaf.value === 'string' && leaf.value.trim() !== '';
  if (DAY_OPERATORS.has(leaf.operator))
    return (
      typeof leaf.value === 'number' &&
      Number.isInteger(leaf.value) &&
      leaf.value >= 1 &&
      leaf.value <= 3650
    );
  if (leaf.operator === 'before' || leaf.operator === 'after')
    return isBranchDate(leaf.value);
  if (Array.isArray(leaf.value)) return leaf.value.length > 0;
  return (
    leaf.value !== undefined &&
    leaf.value !== null &&
    String(leaf.value).trim() !== ''
  );
}

/** Only one group level is accepted. Malformed saved data stays repairable. */
export function branchLeaves(
  group: BranchFilterGroup | null | undefined
): BranchFilterCondition[] {
  if (!Array.isArray(group?.conditions)) return [];
  return group.conditions.flatMap((item) =>
    item && typeof item === 'object' && 'conditions' in item
      ? Array.isArray(item.conditions)
        ? item.conditions.filter(
            (leaf) =>
              leaf && typeof leaf === 'object' && !('conditions' in leaf)
          )
        : []
      : item && typeof item === 'object'
        ? [item]
        : []
  );
}

export function branchConditionsComplete(
  group: BranchFilterGroup | null | undefined
): boolean {
  if (
    !group ||
    !['AND', 'OR'].includes(group.operator) ||
    !Array.isArray(group.conditions) ||
    !group.conditions.length
  )
    return false;
  const leaves = branchLeaves(group);
  return (
    leaves.length <= 20 &&
    group.conditions.every((item) => {
      if (!item || typeof item !== 'object') return false;
      if ('conditions' in item)
        return (
          ['AND', 'OR'].includes(item.operator) &&
          Array.isArray(item.conditions) &&
          !!item.conditions.length &&
          item.conditions.every(
            (leaf) =>
              leaf &&
              typeof leaf === 'object' &&
              !('conditions' in leaf) &&
              branchConditionComplete(leaf)
          )
        );
      return branchConditionComplete(item);
    })
  );
}

export function summarizeBranchConditions(
  group: BranchFilterGroup | null | undefined,
  fields: readonly PlayFieldDescriptor[] = [],
  /** List names by id; an unknown list reads as "a saved list". */
  listNames: Readonly<Record<string, string>> = {}
): string {
  if (!group || !Array.isArray(group.conditions)) return '';
  const labels = new Map(fields.map((field) => [field.id, field.label]));
  const leafSummary = (leaf: BranchFilterCondition) => {
    if (!leaf || typeof leaf.field !== 'string')
      return 'Condition needs repair';
    const label = labels.get(leaf.field) ?? leaf.field;
    if (DAY_OPERATORS.has(leaf.operator)) {
      const prefix =
        leaf.operator === 'within_last_days'
          ? 'in the last'
          : leaf.operator === 'within_next_days'
            ? 'in the next'
            : 'more than';
      return `${label} ${prefix} ${leaf.value ?? '?'} days${leaf.operator === 'more_than_days_ago' ? ' ago' : ''}`;
    }
    if (LIST_OPERATORS.includes(leaf.operator)) {
      const name =
        typeof leaf.value === 'string' &&
        Object.prototype.hasOwnProperty.call(listNames, leaf.value)
          ? listNames[leaf.value]
          : undefined;
      const not = leaf.operator === 'not_in_list' ? ' not' : '';
      return name
        ? `${label} is${not} in list ${name}`
        : `${label} is${not} in a saved list`;
    }
    const value =
      leaf.operator === 'exists' || leaf.operator === 'not_exists'
        ? ''
        : ` ${Array.isArray(leaf.value) ? leaf.value.join(', ') : String(leaf.value ?? '')}`;
    return `${label} ${operatorLabel(leaf.operator).toLowerCase()}${value}`;
  };
  const summary = group.conditions
    .map((item) =>
      item && typeof item === 'object' && 'conditions' in item
        ? `(${Array.isArray(item.conditions) ? item.conditions.map(leafSummary).join(` ${String(item.operator).toLowerCase()} `) : 'Group needs repair'})`
        : leafSummary(item)
    )
    .join(` ${String(group.operator).toLowerCase()} `);
  return summary.length <= 80 ? summary : `${summary.slice(0, 77)}...`;
}

export function extendedBranchConditions(
  group: BranchFilterGroup | null | undefined
): boolean {
  return (
    Array.isArray(group?.conditions) &&
    group.conditions.some(
      (item) =>
        item &&
        typeof item === 'object' &&
        ('conditions' in item ||
          DAY_OPERATORS.has(item.operator) ||
          LIST_OPERATORS.includes(item.operator) ||
          item.operator === 'before' ||
          item.operator === 'after')
    )
  );
}
